import React, { useCallback, useContext, useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { FaWhatsapp, FaEnvelope } from 'react-icons/fa';
import api from '../services/api';
import { AuthContext } from '../context/AuthContext';
import { log } from '../utils/logger';
import './AdminMessages.css';

const PREFERRED_CONTACT_LABELS = {
  whatsapp: 'WhatsApp ou ligação',
  email: 'E-mail'
};

const getPhoneDigits = (phone) => String(phone || '').replace(/\D/g, '');

const getWhatsAppLink = (phone) => {
  const digits = getPhoneDigits(phone);
  if (digits.length < 10) return null;
  const whatsappNumber = digits.startsWith('55') ? digits : `55${digits}`;
  return `https://wa.me/${whatsappNumber}`;
};

const AdminMessages = () => {
  const { user, loading: authLoading } = useContext(AuthContext);
  const navigate = useNavigate();
  const [messages, setMessages] = useState([]);
  const [loading, setLoading] = useState(true);
  const [message, setMessage] = useState('');
  const [filter, setFilter] = useState('all');
  const [inbox, setInbox] = useState('active');

  const fetchMessages = useCallback(async () => {
    try {
      setLoading(true);
      const archived = inbox === 'archived';
      const response = await api.get('/contact-messages/admin', {
        params: { archived }
      });
      setMessages(response.data?.messages || []);
    } catch (error) {
      log.error('Erro ao carregar mensagens:', error);
      setMessage(error.response?.data?.error || 'Erro ao carregar mensagens recebidas.');
      if (error.response?.status === 401) navigate('/admin');
      if (error.response?.status === 403) navigate('/');
    } finally {
      setLoading(false);
    }
  }, [navigate, inbox]);

  useEffect(() => {
    if (authLoading) return;
    if (!user) {
      navigate('/admin');
      return;
    }
    if (!user.isAdmin) {
      navigate('/');
      return;
    }
    fetchMessages();
  }, [user, authLoading, navigate, fetchMessages]);

  const toggleResponded = async (id, currentStatus) => {
    try {
      await api.patch(`/contact-messages/admin/${id}/responded`, { responded: !currentStatus });
      setMessage(!currentStatus ? 'Mensagem marcada como respondida.' : 'Mensagem marcada como pendente.');
      fetchMessages();
    } catch (error) {
      setMessage(error.response?.data?.error || 'Erro ao atualizar status da mensagem.');
    }
  };

  const toggleArchive = async (id, currentlyArchived) => {
    try {
      await api.patch(`/contact-messages/admin/${id}/archive`, { archived: !currentlyArchived });
      setMessage(currentlyArchived ? 'Mensagem restaurada para a caixa de entrada.' : 'Mensagem arquivada.');
      fetchMessages();
    } catch (error) {
      setMessage(error.response?.data?.error || 'Erro ao arquivar mensagem.');
    }
  };

  const deleteMessage = async (id) => {
    if (!window.confirm('Tem certeza que deseja excluir esta mensagem permanentemente?')) {
      return;
    }
    try {
      await api.delete(`/contact-messages/admin/${id}`);
      setMessage('Mensagem excluída com sucesso.');
      fetchMessages();
    } catch (error) {
      setMessage(error.response?.data?.error || 'Erro ao excluir mensagem.');
    }
  };

  const filteredMessages = useMemo(() => {
    if (filter === 'pending') return messages.filter((item) => !item.is_responded);
    if (filter === 'answered') return messages.filter((item) => item.is_responded);
    return messages;
  }, [messages, filter]);

  if (loading) {
    return <div className="admin-messages-page"><div className="loading">Carregando...</div></div>;
  }

  return (
    <div className="admin-messages-page">
      <div className="container">
        <div className="admin-header">
          <h1>MENSAGENS RECEBIDAS</h1>
        </div>

        {message && (
          <div className={message.toLowerCase().includes('erro') ? 'error' : 'success'}>
            {message}
          </div>
        )}

        <div className="admin-messages-toolbar">
          <label htmlFor="message-inbox-filter">Caixa:</label>
          <select
            id="message-inbox-filter"
            value={inbox}
            onChange={(event) => {
              setInbox(event.target.value);
              setFilter('all');
            }}
          >
            <option value="active">Entrada</option>
            <option value="archived">Arquivadas</option>
          </select>

          <label htmlFor="message-status-filter">Status:</label>
          <select
            id="message-status-filter"
            value={filter}
            onChange={(event) => setFilter(event.target.value)}
          >
            <option value="all">Todas</option>
            <option value="pending">Pendentes</option>
            <option value="answered">Respondidas</option>
          </select>
        </div>

        {filteredMessages.length > 0 ? (
          <div className="admin-messages-list">
            {filteredMessages.map((item) => {
              const whatsappLink = getWhatsAppLink(item.phone);
              const hasEmail = Boolean(item.email);

              return (
                <article key={item.id} className={`admin-message-card ${item.is_responded ? 'answered' : 'pending'}`}>
                  <div className="admin-message-header">
                    <h3>{item.name}</h3>
                    <span className={`status-badge ${item.is_responded ? 'answered' : 'pending'}`}>
                      {item.is_responded ? 'Respondida' : 'Pendente'}
                    </span>
                  </div>

                  <div className="admin-message-contact-row">
                    <p><strong>Telefone:</strong> {item.phone}</p>
                    {whatsappLink && (
                      <a
                        href={whatsappLink}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="admin-message-quick-btn admin-message-quick-btn-whatsapp"
                        title="Abrir WhatsApp"
                      >
                        <FaWhatsapp aria-hidden="true" />
                        WhatsApp
                      </a>
                    )}
                  </div>

                  <div className="admin-message-contact-row">
                    <p><strong>E-mail:</strong> {item.email || 'Não informado'}</p>
                    {hasEmail && (
                      <a
                        href={`mailto:${item.email}`}
                        className="admin-message-quick-btn admin-message-quick-btn-email"
                        title="Enviar e-mail"
                      >
                        <FaEnvelope aria-hidden="true" />
                        E-mail
                      </a>
                    )}
                  </div>

                  <p>
                    <strong>Preferência de contato:</strong>{' '}
                    {PREFERRED_CONTACT_LABELS[item.preferred_contact] || item.preferred_contact || 'Não informado'}
                  </p>
                  <p><strong>Recebida em:</strong> {new Date(item.created_at).toLocaleString('pt-BR')}</p>
                  <p className="admin-message-content"><strong>Mensagem:</strong> {item.message}</p>

                  {item.is_responded && (
                    <p className="admin-message-meta">
                      Respondida por {item.responded_by || 'admin'} em{' '}
                      {item.responded_at ? new Date(item.responded_at).toLocaleString('pt-BR') : '-'}
                    </p>
                  )}

                  <div className="admin-message-actions">
                    <button
                      type="button"
                      className={`admin-action-btn ${item.is_responded ? 'admin-action-btn-muted' : 'admin-action-btn-primary'}`}
                      onClick={() => toggleResponded(item.id, item.is_responded)}
                      title={item.is_responded ? 'Marcar como pendente' : 'Marcar como respondida'}
                    >
                      {item.is_responded ? 'Pendente' : 'Respondida'}
                    </button>
                    <button
                      type="button"
                      className="admin-action-btn admin-action-btn-muted"
                      onClick={() => toggleArchive(item.id, item.is_archived)}
                      title={item.is_archived ? 'Restaurar para entrada' : 'Arquivar mensagem'}
                    >
                      {item.is_archived ? 'Restaurar' : 'Arquivar'}
                    </button>
                    <button
                      type="button"
                      className="admin-action-btn admin-action-btn-danger"
                      onClick={() => deleteMessage(item.id)}
                      title="Excluir mensagem"
                    >
                      Excluir
                    </button>
                  </div>
                </article>
              );
            })}
          </div>
        ) : (
          <p className="admin-messages-empty">
            {inbox === 'archived'
              ? 'Nenhuma mensagem arquivada.'
              : 'Nenhuma mensagem encontrada para esse filtro.'}
          </p>
        )}
      </div>
    </div>
  );
};

export default AdminMessages;
